/* eslint-disable react/jsx-props-no-spreading */
import { useTranslation } from "react-i18next";
import { useAutoCompleteEirCode } from "./useAutoCompleteEirCode";
import type { FieldValues, UseFormMethods } from "react-hook-form";
import { Controller } from "react-hook-form";
import { IAddress } from "@interface/customerDetails/IAddress";
import { Autocomplete, CircularProgress } from "@mui/material";
import { eircodeRegex } from "@utils/regex";
import { EircodeHelperText } from "@features/eircode/common/EircodeHelperText/EircodeHelperText";
import { MutableRefObject, useMemo } from "react";
import { LabeledTextField } from "@common/LabeledTextField/LabeledTextField";

interface IParams<TFieldValues extends FieldValues> {
    addressForm: UseFormMethods<TFieldValues> & Partial<UseFormMethods>;
    inputRef?: MutableRefObject<HTMLInputElement | null>;
    prefix?: string;
    getSelectedAddress?(address: IAddress): void;
}

export const AutoCompleteEirCode = <TFieldValues extends FieldValues>(props: IParams<TFieldValues>) => {
    const { addressForm, inputRef, prefix, getSelectedAddress } = props;
    const { t } = useTranslation("address");

    const {
        formErrors,
        namePrefix,
        handleChange,
        handleInputChange,
        getDefaultMessage,
        getOptionLabel,
        getFilterOptions,
        eircodeAddressList,
        getEircodeAddressListState,
    } = useAutoCompleteEirCode({
        addressForm,
        prefix,
        getSelectedAddress,
    });

    const hasOptionClassName = useMemo(
        () => (eircodeAddressList?.length ?? getEircodeAddressListState.isLoading ? "has-options" : undefined),
        [eircodeAddressList?.length, getEircodeAddressListState.isLoading]
    );

    return (
        <Controller
            control={addressForm.control}
            name={`${namePrefix}code`}
            render={(controllerRenderProps) => (
                <Autocomplete
                    blurOnSelect
                    className={hasOptionClassName}
                    classes={{
                        paper: hasOptionClassName,
                    }}
                    disableClearable
                    filterOptions={getFilterOptions}
                    freeSolo
                    getOptionLabel={getOptionLabel}
                    loading={getEircodeAddressListState.isLoading}
                    noOptionsText={getDefaultMessage()}
                    options={eircodeAddressList ?? []}
                    renderInput={(renderInputParams) => (
                        <LabeledTextField
                            {...renderInputParams}
                            InputProps={{
                                ...renderInputParams.InputProps,
                                endAdornment: (
                                    <>
                                        {getEircodeAddressListState.isLoading && <CircularProgress size={20} />}
                                        {renderInputParams.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                            error={Boolean(formErrors?.code)}
                            fullWidth
                            helperText={formErrors?.code?.message ?? <EircodeHelperText />}
                            id="enter-eircode"
                            inputRef={(element: HTMLInputElement | null) => {
                                if (inputRef) {
                                    inputRef.current = element;
                                }
                            }}
                            isRequired
                            labelText={t("enterEircode")}
                            placeholder={t("enterYourEircode")}
                        />
                    )}
                    value={controllerRenderProps.value ?? null}
                    onChange={(_, newValue: IAddress | string) => {
                        handleChange(newValue);
                    }}
                    onInputChange={(_, newInputValue, reason) => {
                        handleInputChange(controllerRenderProps, newInputValue, reason);
                    }}
                />
            )}
            rules={{
                pattern: { value: eircodeRegex, message: t("validEircode") },
                required: t("validEircode"),
            }}
        />
    );
};
